import type { ANALYTICS_MEASURE_TYPE } from "../../scheme/components/analytics/interfaces";
import { MULTI_ANALYSIS_MEASURE_TYPE } from "../types";
import { mapMultiAnalysisMeasureTypeToTrendDataAnalytics } from "./measure-type-map";
import { createPromiseCache } from "./promise-cache";
import {
  type IMultiAnalysisMeasureDataResponse,
  normalizeMultiAnalysisMeasureOptions,
  type IMultiAnalysisMeasureOption,
  type IMultiAnalysisRequestTimeRange,
} from "./trend-series";

interface IMultiAnalysisMeasureApi {
  fetchJson: (args: { url: string; method: "GET"; data: Record<string, unknown> }) => Promise<unknown>;
}

const measureOptionsCache = createPromiseCache<IMultiAnalysisMeasureOption[]>();
const measureDataCache = createPromiseCache<IMultiAnalysisMeasureDataResponse | null | undefined>();

const normalizePointIds = (pointIds: readonly number[]): number[] =>
  Array.from(new Set(pointIds)).sort((left, right) => left - right);

const buildMeasureOptionsCacheKey = (measureType: ANALYTICS_MEASURE_TYPE, pointIds: readonly number[]): string =>
  `${measureType}:${pointIds.join(",")}`;

const normalizeMeasureNames = (measureNames: readonly string[]): string[] =>
  Array.from(new Set(measureNames)).sort((left, right) => left.localeCompare(right));

const buildMeasureDataCacheKey = (
  measureType: ANALYTICS_MEASURE_TYPE,
  pointIds: readonly number[],
  measureNames: readonly string[],
  range: IMultiAnalysisRequestTimeRange,
): string =>
  JSON.stringify({
    measureType,
    pointIds,
    measureNames,
    range,
  });

export const fetchMultiAnalysisMeasureOptions = (
  api: IMultiAnalysisMeasureApi,
  measureType: ANALYTICS_MEASURE_TYPE,
  pointIds: readonly number[],
): Promise<IMultiAnalysisMeasureOption[]> => {
  const normalizedPointIds = normalizePointIds(pointIds);

  if (!normalizedPointIds.length) {
    return Promise.resolve([]);
  }

  const cacheKey = buildMeasureOptionsCacheKey(measureType, normalizedPointIds);
  return measureOptionsCache.getOrCreate(cacheKey, () =>
    api
      .fetchJson({
        url: "/point-v2/measure/autocomplete",
        method: "GET",
        data: {
          measureType,
          points: JSON.stringify(normalizedPointIds),
        },
      })
      .then(normalizeMultiAnalysisMeasureOptions),
  );
};

export const mergeMultiAnalysisMeasureDataResponses = (
  responses: readonly (IMultiAnalysisMeasureDataResponse | null | undefined)[],
): IMultiAnalysisMeasureDataResponse => {
  const merged: IMultiAnalysisMeasureDataResponse = {};
  let index = 0;

  responses.forEach(response => {
    Object.values(response ?? {}).forEach(measure => {
      if (!measure?.data?.length) {
        return;
      }

      merged[`measureData${index + 1}`] = measure;
      index += 1;
    });
  });

  return merged;
};

export const fetchMultiAnalysisMeasureData = (
  api: IMultiAnalysisMeasureApi,
  {
    measureType,
    pointIds,
    measureNames,
    range,
  }: {
    measureType: ANALYTICS_MEASURE_TYPE;
    pointIds: readonly number[];
    measureNames: readonly string[];
    range: IMultiAnalysisRequestTimeRange;
  },
): Promise<IMultiAnalysisMeasureDataResponse | null | undefined> => {
  const normalizedPointIds = normalizePointIds(pointIds);
  const normalizedMeasureNames = normalizeMeasureNames(measureNames);

  if (!normalizedPointIds.length || !normalizedMeasureNames.length) {
    return Promise.resolve({});
  }

  const cacheKey = buildMeasureDataCacheKey(measureType, normalizedPointIds, normalizedMeasureNames, range);
  return measureDataCache.getOrCreate(
    cacheKey,
    () =>
      api.fetchJson({
        url: "/point-v2/measure/data",
        method: "GET",
        data: {
          points: JSON.stringify(normalizedPointIds),
          measureNames: JSON.stringify(normalizedMeasureNames),
          startTimestamp: range.startTimestamp,
          endTimestamp: range.endTimestamp,
          measureType,
        },
      }) as Promise<IMultiAnalysisMeasureDataResponse | null | undefined>,
  );
};

export const fetchMultiAnalysisMeasureDataForNames = (
  api: IMultiAnalysisMeasureApi,
  {
    measureType,
    pointIds,
    measureNames,
    range,
  }: {
    measureType: ANALYTICS_MEASURE_TYPE;
    pointIds: readonly number[];
    measureNames: readonly string[];
    range: IMultiAnalysisRequestTimeRange;
  },
): Promise<IMultiAnalysisMeasureDataResponse> => {
  const normalizedMeasureNames = normalizeMeasureNames(measureNames);

  if (!normalizedMeasureNames.length) {
    return Promise.resolve({});
  }

  return Promise.all(
    normalizedMeasureNames.map(name =>
      fetchMultiAnalysisMeasureData(api, {
        measureType,
        pointIds,
        measureNames: [name],
        range,
      }),
    ),
  ).then(mergeMultiAnalysisMeasureDataResponses);
};

export const fetchMultiAnalysisTrendMeasureData = (
  api: IMultiAnalysisMeasureApi,
  {
    multiAnalysisMeasureType,
    pointIds,
    measureNames,
    range,
  }: {
    multiAnalysisMeasureType: MULTI_ANALYSIS_MEASURE_TYPE;
    pointIds: readonly number[];
    measureNames: readonly string[];
    range: IMultiAnalysisRequestTimeRange;
  },
): Promise<IMultiAnalysisMeasureDataResponse> => {
  const trendDataMeasureType = mapMultiAnalysisMeasureTypeToTrendDataAnalytics(multiAnalysisMeasureType);

  if (
    multiAnalysisMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM ||
    multiAnalysisMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM
  ) {
    return fetchMultiAnalysisMeasureDataForNames(api, {
      measureType: trendDataMeasureType,
      pointIds,
      measureNames,
      range,
    });
  }

  return fetchMultiAnalysisMeasureData(api, {
    measureType: trendDataMeasureType,
    pointIds,
    measureNames,
    range,
  }).then(response => response ?? {});
};
